import { forwardRef, useState } from 'react';
import { Input, InputProps } from '../../atoms/Input';
import Icon from '../../atoms/Icon/Icon';
import './FormField.scss';
import { useCurrency } from '@/contexts/CurrencyContext';

export interface FormFieldProps extends Omit<InputProps, 'className' | 'type'> {
  label?: string;
  error?: string;
  helperText?: string;
  required?: boolean;
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
  errorClassName?: string;
  helperClassName?: string;
  lang?: string;
  checked?: boolean;
  children?: React.ReactNode; // For select options
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search' | 'date' | 'time' | 'datetime-local' | 'select'  | 'textarea' | 'checkbox' | 'price';
  rows?: number; // For textarea
  prefix?: string;
  suffix?: string;
  onChange?: (event: React.ChangeEvent<any>) => void;
}

const FormField = forwardRef<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement, FormFieldProps>(
  (
    {
      label,
      error,
      helperText,
      required = false,
      className = '',
      labelClassName = '',
      inputClassName = '',
      errorClassName = '',
      helperClassName = '',
      lang,
      id,
      type = 'text',
      children,
      onChange,
      checked,
      rows,
      prefix,
      suffix,
      ...inputProps
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const fieldId = id || `field-${Math.random().toString(36).substr(2, 9)}`;
    const errorId = `${fieldId}-error`;
    const helperId = `${fieldId}-helper`;
    const { currency } = useCurrency();

    const hasError = !!error;
    const inputVariant = hasError ? 'error' : inputProps.variant || 'default';
    
    // Determine the actual input type (for password toggle)
    const actualInputType = type === 'password' && showPassword ? 'text' : type;
    const isPasswordField = type === 'password';

    // If type is select, render a select element
    if (type === 'select') {
      // Remove props that don't apply to select elements
      const { 
        size, 
        variant, 
        placeholder, 
        autoComplete, 
        autoFocus, 
        maxLength, 
        minLength, 
        max, 
        min, 
        step,
        pattern, 
        title,
        onBlur,
        onFocus,
        onKeyDown,
        onKeyUp,
        ...selectProps 
      } = inputProps;
      const baseClass = 'input';
      const sizeClass = `input--${size}`;
      const variantClass = variant ? `input--${variant}` : '';
  
      // if className is empty, add the input default classes
      const selectClasses = inputClassName === '' ? [
        baseClass,
        sizeClass,
        variantClass,
        inputClassName,
      ]
        .filter(Boolean)
        .join(' ') : inputClassName;

      return (
        <div className={`form-field ${className}`}>
          {label && (
            <label
              htmlFor={fieldId}
              className={`form-field__label ${labelClassName}`}
            >
              {label}
              {required && <span className="form-field__required">*</span>}
            </label>
          )}
          
          <select
            ref={ref as React.Ref<HTMLSelectElement>}
            id={fieldId}
            className={`form-field__select form-field__input ${selectClasses}`}
            aria-describedby={
              [hasError ? errorId : '', helperText ? helperId : '']
                .filter(Boolean)
                .join(' ')
            }
            onChange={onChange as React.ChangeEventHandler<HTMLSelectElement>}
            {...selectProps}
          >
            {children}
          </select>
          
          {hasError && (
            <div
              id={errorId}
              className={`form-field__error ${errorClassName}`}
              role="alert"
            >
              {error}
            </div>
          )}
          
          {helperText && !hasError && (
            <div
              id={helperId}
              className={`form-field__helper ${helperClassName}`}
            >
              {helperText}
            </div>
          )}
        </div>
      );
    }

    // If type is textarea, render a textarea element
    if (type === 'textarea') {
      // Remove props that don't apply to textarea elements
      const { 
        size, 
        variant, 
        autoComplete, 
        autoFocus, 
        maxLength, 
        minLength, 
        max, 
        min, 
        step,
        pattern, 
        title,
        onBlur,
        onFocus,
        onKeyDown,
        onKeyUp,
        ...textareaProps 
      } = inputProps;
      
      return (
        <div className={`form-field ${className}`}>
          {label && (
            <label
              htmlFor={fieldId}
              className={`form-field__label ${labelClassName}`}
            >
              {label}
              {required && <span className="form-field__required">*</span>}
            </label>
          )}
          
          <textarea
            ref={ref as React.Ref<HTMLTextAreaElement>}
            id={fieldId}
            className={`form-field__textarea form-field__input ${inputClassName}`}
            aria-describedby={
              [hasError ? errorId : '', helperText ? helperId : '']
                .filter(Boolean)
                .join(' ')
            }
            onChange={onChange as React.ChangeEventHandler<HTMLTextAreaElement>}
            rows={rows || 3}
            {...textareaProps}
          />
          
          {hasError && (
            <div
              id={errorId}
              className={`form-field__error ${errorClassName}`}
              role="alert"
            >
              {error}
            </div>
          )}
          
          {helperText && !hasError && (
            <div
              id={helperId}
              className={`form-field__helper ${helperClassName}`}
            >
              {helperText}
            </div>
          )}
        </div>
      );
    }

    // Price input rendering with currency prefix and leading zero handling
    if (type == 'price') {
      const sanitize = (val: string) => {
        let raw = val;
        raw = val.replace(/[^\d.,]/g, '').replace(/,/g, '.');
        if (raw.startsWith('0') && !raw.startsWith('0.')) {
          raw = raw.replace(/^0+(?=\d)/, '');
        }
        if (raw.startsWith('.')) raw = `0${raw}`;
        return raw;
      };

      const displayedValue = (() => {
        if (typeof inputProps.value === 'number') {
          return inputProps.value === 0 ? '' : String(inputProps.value);
        }
        if (typeof inputProps.value === 'string') {
          return inputProps.value;
        }
        return '';
      })();

      return (
        <div className={`form-field ${className}`}>
          {label && (
            <label
              htmlFor={fieldId}
              className={`form-field__label ${labelClassName}`}
            >
              {label}
              {required && <span className="form-field__required">*</span>}
            </label>
          )}

          <div className="form-field__price-wrapper">
            {prefix && (<span className="form-field__price-prefix" aria-hidden="true">{prefix}</span>)}
            <input
              ref={ref as React.Ref<HTMLInputElement>}
              id={fieldId}
              type="text"
              inputMode="decimal"
              pattern="[0-9]*[.,]?[0-9]*"
              className={`form-field__input input ${inputClassName}`}
              aria-describedby={
                [hasError ? errorId : '', helperText ? helperId : '']
                  .filter(Boolean)
                  .join(' ')
              }
              placeholder={inputProps.placeholder || '0.00'}
              value={displayedValue}
              onChange={(e) => {
                const raw = sanitize(e.target.value);
                const syntheticEvent = { ...e, target: { ...e.target, value: raw } } as React.ChangeEvent<any>;
                onChange && onChange(syntheticEvent);
              }}
              disabled={inputProps.disabled}
            />
            {suffix && (<span className="form-field__price-suffix" aria-hidden="true">{suffix}</span>)} 
          </div>

          {hasError && (
            <div
              id={errorId}
              className={`form-field__error ${errorClassName}`}
              role="alert"
            >
              {error}
            </div>
          )}

          {helperText && !hasError && (
            <div
              id={helperId}
              className={`form-field__helper ${helperClassName}`}
            >
              {helperText}
            </div>
          )}
        </div>
      );
    }

    // Default Input rendering
    return (
      <div className={`form-field ${className}`}>
        {label && (
          <label
            htmlFor={fieldId}
            className={`form-field__label ${labelClassName}`}
          >
            {label}
            {required && <span className="form-field__required">*</span>}
          </label>
        )}
        
        <div className={`form-field__input-wrapper ${isPasswordField ? 'form-field__input-wrapper--password' : ''}`}>
          <Input
            ref={ref as React.Ref<HTMLInputElement>}
            id={fieldId}
            type={actualInputType}
            checked={checked}
            variant={inputVariant}
            className={`form-field__input ${inputClassName} ${isPasswordField ? 'form-field__input--with-toggle' : ''}`}
            aria-describedby={
              [hasError ? errorId : '', helperText ? helperId : '']
                .filter(Boolean)
                .join(' ')
            }
            onChange={onChange as React.ChangeEventHandler<HTMLInputElement>}
            {...inputProps}
          />
          
          {isPasswordField && (
            <button
              type="button"
              className="form-field__password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              tabIndex={-1}
            >
              <Icon 
                name={showPassword ? 'ojo-cerrado' : 'ojo'} 
                size={20} 
                color="var(--white)"
                alt={showPassword ? 'Hide password' : 'Show password'}
              />
            </button>
          )}
        </div>
        
        {hasError && (
          <div
            id={errorId}
            className={`form-field__error ${errorClassName}`}
            role="alert"
          >
            {error}
          </div>
        )}
        
        {helperText && !hasError && (
          <div
            id={helperId}
            className={`form-field__helper ${helperClassName}`}
          >
            {helperText}
          </div>
        )}
      </div>
    );
  }
);

FormField.displayName = 'FormField';

export default FormField; 